import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { format } from 'date-fns';
import { consumptionTypeLabels } from 'lib/constants';
import { getSupplierIcon, getSupplierInfoFirebase } from 'lib/firebase';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { SupplierInfo } from 'types';
import { Ticket } from 'types/User/firebaseTypes/ticket';
import { DeviceLookup } from 'views/Planning/Planning';
import styles from './DeliveriesPrintModal.module.scss';
import { IssueCauseIcon } from 'components/common/IssueCauseIcon/IssueCauseIcon';

interface DeliveriesPrintModalProps {
    deliveries: Ticket[];
    selectedTickets: string[];
    modalOpen: boolean;
    setModalOpen: (t: boolean) => void;
    /** A supplierId for the supplier that an admin user wants to view data for. */
    viewAsSupplier: string;
    devicesList: DeviceLookup;
}

export const DeliveriesPrintModal = ({
    deliveries,
    selectedTickets,
    modalOpen,
    setModalOpen,
    viewAsSupplier,
    devicesList,
}: DeliveriesPrintModalProps) => {
    const [supplierInfo, setSupplierInfo] = useState<SupplierInfo | null>(null);
    const [filteredTickets, setFilteredTickets] = useState<
        (Ticket | undefined)[]
    >([]);
    const [selectedDriver, setSelectedDriver] = useState<string>('');
    const [availableDrivers, setAvailableDrivers] = useState<string[]>([]);
    const [iconUrl, setIconUrl] = useState<string>('');

    const usersList = useSelector(
        (state: RootState) => state.authentication?.newPortalUsers
    );

    useEffect(() => {
        if (viewAsSupplier) {
            getSupplierInfoFirebase(viewAsSupplier).then((supplierInfo) => {
                setSupplierInfo(supplierInfo);
            });
            getSupplierIcon(viewAsSupplier)
                .then((iconUrl: string) => {
                    setIconUrl(iconUrl);
                })
                .catch(() => setIconUrl(''));
        }
    }, [viewAsSupplier, setSupplierInfo]);

    useEffect(() => {
        if (selectedTickets.length && deliveries && modalOpen) {
            const drivers: string[] = [];
            const ticketsToPrint = selectedTickets.map((ticket) => {
                const matchingTicket = deliveries.find(
                    (delivery) => delivery.id === ticket
                );

                if (
                    matchingTicket?.taggedUsers?.[0]?.email &&
                    !drivers.includes(matchingTicket.taggedUsers[0].email)
                ) {
                    drivers.push(matchingTicket.taggedUsers[0].email);
                }

                return matchingTicket;
            });

            setAvailableDrivers(drivers);

            if (selectedDriver) {
                setFilteredTickets(
                    ticketsToPrint.filter(
                        (ticket) =>
                            ticket?.taggedUsers?.[0]?.email === selectedDriver
                    )
                );
            } else {
                setFilteredTickets(ticketsToPrint);
            }
        } else {
            setAvailableDrivers([]);
            setFilteredTickets([]);
        }
    }, [
        selectedTickets,
        deliveries,
        selectedDriver,
        modalOpen,
        setFilteredTickets,
    ]);

    return (
        <Dialog
            open={modalOpen}
            onClose={() => {
                setSelectedDriver('');
                setModalOpen(false);
            }}
            maxWidth="md"
            fullWidth={true}
            data-testid="DeliveriesPrintModal"
        >
            <DialogTitle>Print Selected Tickets</DialogTitle>
            <DialogContent>
                <div className={styles.container}>
                    {!selectedTickets.length && (
                        <Alert
                            severity="info"
                            data-testid="DeliveriesPrintAlert"
                        >
                            Please select tickets that you would like to print.
                        </Alert>
                    )}
                    {!!selectedTickets.length && !filteredTickets.length && (
                        <div className={styles.printLoading}>
                            <div>
                                <CircularProgress />
                            </div>
                        </div>
                    )}
                    {filteredTickets.map((ticket, index) => {
                        if (!ticket) {
                            return (
                                <div key={index}>
                                    Error printing ticket data
                                </div>
                            );
                        }

                        const device = devicesList[ticket.deviceId];

                        return (
                            <div
                                className={styles.ticketPrintItem}
                                key={ticket?.id}
                                data-testid="TicketPrintItem"
                            >
                                <Grid2 container spacing={0}>
                                    {/* Header */}
                                    <Grid2 xs={12}>
                                        <div className={styles.cardHeader}>
                                            {iconUrl && (
                                                <img
                                                    style={{
                                                        maxHeight: '60px',
                                                    }}
                                                    src={iconUrl}
                                                    alt="Supplier Logo"
                                                />
                                            )}
                                            <strong>
                                                {supplierInfo?.name}
                                            </strong>
                                            <div>{supplierInfo?.address}</div>
                                        </div>
                                    </Grid2>

                                    {/* Row One */}
                                    <Grid2 xs={3}>
                                        <div className={styles.cardBlock}>
                                            <strong>Tank Name</strong>
                                            <div>{device?.name ?? 'N/A'}</div>
                                        </div>
                                    </Grid2>
                                    <Grid2 xs={6}>
                                        <div className={styles.cardBlock}>
                                            <strong>Address</strong>
                                            <div>
                                                {device?.address ?? 'N/A'}
                                            </div>
                                        </div>
                                    </Grid2>
                                    <Grid2 xs={3}>
                                        <div className={styles.cardBlock}>
                                            <strong>Customer</strong>
                                            <div>
                                                {device?.customer_name ?? 'N/A'}
                                            </div>
                                        </div>
                                    </Grid2>

                                    {/* Row Two */}
                                    <Grid2 xs={4}>
                                        <div className={styles.cardBlock}>
                                            <strong>Created By</strong>
                                            <div>
                                                {(ticket?.createdBy &&
                                                    usersList?.[
                                                        ticket.createdBy
                                                    ]?.email) ??
                                                    ticket?.createdBy ??
                                                    'N/A'}
                                            </div>
                                        </div>
                                    </Grid2>
                                    <Grid2 xs={4}>
                                        <div className={styles.cardBlock}>
                                            <strong>Date Created</strong>
                                            <div>
                                                {ticket?.dateCreated_iso
                                                    ? format(
                                                          new Date(
                                                              ticket.dateCreated_iso
                                                          ),
                                                          'MM/dd/yyyy'
                                                      )
                                                    : 'N/A'}
                                            </div>
                                        </div>
                                    </Grid2>
                                    <Grid2 xs={4}>
                                        <div className={styles.cardBlock}>
                                            <strong>
                                                Planned Delivery Date
                                            </strong>
                                            <div>
                                                {ticket?.plannedDate ||
                                                device?.duf_7
                                                    ? format(
                                                          new Date(
                                                              ticket.plannedDate ??
                                                                  device?.duf_7
                                                          ),
                                                          'MM/dd/yyyy'
                                                      )
                                                    : 'N/A'}
                                            </div>
                                        </div>
                                    </Grid2>

                                    {/* Row Three */}
                                    <Grid2 xs={6}>
                                        <div className={styles.cardBlock}>
                                            <strong>Tank Capacity</strong>
                                            <div>
                                                {device?.capacity ?? 'N/A'}
                                            </div>
                                        </div>
                                    </Grid2>
                                    <Grid2 xs={6}>
                                        <div className={styles.cardBlock}>
                                            <strong>
                                                Estimated Current Tank (gal)
                                            </strong>
                                            <div>
                                                {device?.gallons?.toFixed(2) ??
                                                    'N/A'}
                                            </div>
                                        </div>
                                    </Grid2>

                                    {/* Row Four */}
                                    <Grid2 xs={4}>
                                        <div className={styles.cardBlock}>
                                            <strong>Usage Types</strong>

                                            <div>
                                                {!!device?.consumptionType
                                                    ? Object.entries(
                                                          device?.consumptionType ??
                                                              {}
                                                      )
                                                          .filter(
                                                              (e) =>
                                                                  e[1] === true
                                                          )
                                                          .map(
                                                              (e) =>
                                                                  consumptionTypeLabels[
                                                                      e[0]
                                                                  ]
                                                          )
                                                          .join(', ')
                                                    : 'N/A'}
                                            </div>
                                        </div>
                                    </Grid2>
                                    <Grid2 xs={4}>
                                        <div className={styles.cardBlock}>
                                            <strong>Fuel Type</strong>
                                            <div>
                                                {devicesList[ticket.deviceId]
                                                    ?.fuelType ?? 'N/A'}
                                            </div>
                                        </div>
                                    </Grid2>
                                    <Grid2 xs={4}>
                                        <div className={styles.cardBlock}>
                                            <strong>Last Delivery</strong>
                                            <div>
                                                {ticket?.lastFillup?.time
                                                    ? format(
                                                          new Date(
                                                              ticket.lastFillup.time
                                                          ),
                                                          'MM/dd/yyyy'
                                                      )
                                                    : 'N/A'}
                                            </div>
                                        </div>
                                    </Grid2>

                                    {/* Row Five */}
                                    <Grid2
                                        xs={device?.activeIssueTicket ? 6 : 12}
                                    >
                                        <div className={styles.cardBlock}>
                                            <strong>
                                                Delivery Instructions
                                            </strong>
                                            <div>
                                                {ticket?.delivery_instructions ??
                                                    devicesList[ticket.deviceId]
                                                        ?.delivery_instructions ??
                                                    'N/A'}
                                            </div>
                                        </div>
                                    </Grid2>
                                    {device?.activeIssueTicket && (
                                        <Grid2 xs={6}>
                                            <div className={styles.cardBlock}>
                                                <div
                                                    className={
                                                        styles.issueBlock
                                                    }
                                                >
                                                    <strong>
                                                        Monitor Issue
                                                    </strong>
                                                    <IssueCauseIcon
                                                        fontSize="small"
                                                        trigger={
                                                            device
                                                                .activeIssueTicket
                                                                .ticketTrigger
                                                        }
                                                    />
                                                </div>
                                                <div>
                                                    <b>
                                                        {
                                                            device
                                                                .activeIssueTicket
                                                                .name
                                                        }
                                                        :{' '}
                                                    </b>
                                                    {
                                                        device
                                                            ?.activeIssueTicket
                                                            ?.description
                                                    }
                                                </div>
                                            </div>
                                        </Grid2>
                                    )}
                                </Grid2>
                            </div>
                        );
                    })}
                </div>
                {!!filteredTickets.length && (
                    <DialogActions className={styles.dialogFooter}>
                        <FormControl>
                            <InputLabel
                                id="driver-select"
                                htmlFor="driver-select"
                            >
                                Filter by Driver
                            </InputLabel>
                            <Select
                                variant="standard"
                                name="driver-select"
                                value={selectedDriver}
                                onChange={(e) =>
                                    setSelectedDriver(e.target.value)
                                }
                                sx={{
                                    width: 200,
                                }}
                            >
                                <MenuItem value="">Select a driver</MenuItem>
                                {availableDrivers.map((driver) => (
                                    <MenuItem value={driver} key={driver}>
                                        {driver}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            onClick={() => window.print()}
                        >
                            Print
                        </Button>
                    </DialogActions>
                )}
            </DialogContent>
        </Dialog>
    );
};
